import type { RecommendationType } from '#schemas/widget-schemas.js';

interface EntitySource {
  readonly path: readonly string[];
  readonly idKey: 'id' | '_id';
  readonly type: RecommendationType;
}

const ENTITY_SOURCES: Readonly<Record<string, readonly EntitySource[]>> = {
  fetchEntityData: [{ path: ['entity'], idKey: '_id', type: 'hotel' }],
  fetchDestinationData: [{ path: ['results'], idKey: '_id', type: 'destination' }],
  fetchFlightData: [{ path: ['results'], idKey: 'id', type: 'flight' }],
  fetchHotelData: [
    { path: ['results'], idKey: '_id', type: 'hotel' },
    { path: ['destinations', 'hotels'], idKey: '_id', type: 'hotel' },
  ],
  fetchPlaceData: [{ path: ['places'], idKey: 'id', type: 'place' }],
  fetchPlacesNearby: [{ path: ['places'], idKey: 'id', type: 'place' }],
  fetchRoomAvailability: [{ path: ['rooms'], idKey: 'id', type: 'room' }],
  findPlaces: [{ path: ['places'], idKey: 'id', type: 'place' }],
  resumeFlightSearchWorkflow: [{ path: ['results'], idKey: 'id', type: 'flight' }],
  resumeRoomSearchWorkflow: [{ path: ['rooms'], idKey: 'id', type: 'room' }],
};

function collectAt(value: unknown, path: readonly string[], idKey: string, out: string[]): void {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    for (const item of value) collectAt(item, path, idKey, out);
    return;
  }
  if (path.length === 0) {
    const id = (value as Record<string, unknown>)[idKey];
    if (typeof id === 'string' && id.length > 0) out.push(id);
    return;
  }
  collectAt((value as Record<string, unknown>)[path[0]!], path.slice(1), idKey, out);
}

/** Entity ids a data tool's output surfaced, each with its recommendation type (see `WidgetMarkerInterleaver.setRecommendationTypeResolver`). */
export function collectRecommendationEntities(toolName: string, output: unknown): Map<string, RecommendationType> {
  const found = new Map<string, RecommendationType>();
  const sources = ENTITY_SOURCES[toolName];
  if (!sources || output === null || typeof output !== 'object') return found;
  const roots = [output, (output as Record<string, unknown>)['output']];
  for (const source of sources) {
    for (const root of roots) {
      const ids: string[] = [];
      collectAt(root, source.path, source.idKey, ids);
      for (const id of ids) if (!found.has(id)) found.set(id, source.type);
    }
  }
  return found;
}
